import { Box, Group, Menu, Tooltip } from '@mantine/core';
import { trackEvent } from '@repo/common/analytics';
import { UserRole } from '@repo/common/enums/UserRole';
import {
	Breadcrumbs,
	BreadcrumbsSkeleton,
	Icon,
	IconButton,
	Text,
} from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { queryClient, useAuthUser } from '../../../api';
import {
	useDeleteAutomation,
	useUpdateAutomation,
} from '../../../api/hooks/automation';
import { automationQueryKeyFactory } from '../../../api/hooks/automation/constants';
import RunAutomationButton from '../../../components/Automation/RunAutomationButton';
import { EntityPageSidebarToggle } from '../../../components/EntityPageSidebarToggle';
import {
	closeAllModals,
	openConfirmModal,
} from '../../../components/ModalManager';
import {
	NavBarBreadcrumbWrapper,
	NavBarRightSideWrapper,
	NavBarSidesheetControlsWrapper,
	NavBarWrapper,
} from '../../../components/SecodaAppShell/SecodaNavBar';
import { useCheckIAMPermission } from '../../../utils/authorization/roles';
import { AutomationToggle } from '../AutomationToggle';
import type { IAutomationPageNavBarProps } from './AutomationPageNavbar.types';

function AutomationPageNavBar({
	automation,
	navigateToHistory,
}: IAutomationPageNavBarProps) {
	const navigate = useNavigate();
	const { user, workspace } = useAuthUser();
	const { hasPermission: hasDeletePermission } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN],
		v2Permission: 'Automations.Delete',
	});

	const { mutateAsync: updateAutomation } = useUpdateAutomation({
		// Because we use a MobX store, we do not need to use optimistic updates.
		// This will lead to flickering because of the normy library. Running
		// `invalidateQueries` will let us refetch the latest cards.
		disableOptimisticUpdate: true,
		options: {
			onSuccess: () => {
				// This is necessary to refetch the automation data after
				// updating the automation title
				queryClient.invalidateQueries({
					queryKey: automationQueryKeyFactory.byId(automation?.id ?? ''),
				});
			},
		},
	});

	const { mutateAsync: deleteAutomation } = useDeleteAutomation({
		options: {
			onSuccess: async () => {
				await queryClient.invalidateQueries({
					queryKey: automationQueryKeyFactory.allLists(),
				});
				navigate('/automations');
			},
		},
	});

	const handleAutomationStatusToggle = useCallback(
		async (enabled: boolean) => {
			if (automation?.id) {
				await updateAutomation({
					data: {
						id: automation.id,
						is_enabled: enabled,
					},
				});
			}
		},
		[automation?.id, updateAutomation]
	);

	const deleteAutomationFn = useCallback(
		async (automationId: string): Promise<void> => {
			await deleteAutomation({
				id: automationId,
			});
			trackEvent('automation/delete', {}, user, workspace);
			closeAllModals();
		},
		[deleteAutomation, user, workspace]
	);

	const handleDeleteAutomation = useCallback(async () => {
		if (automation?.id) {
			openConfirmModal({
				confirmProps: {
					variant: 'primary',
					tone: 'critical',
				},
				confirmLabel: 'Delete Automation',
				onConfirm: async () => {
					await deleteAutomationFn(automation.id);
				},
				title: `Delete Automation?`,
				children: <Text>You can&apos;t undo this action.</Text>,
			});
		}
	}, [automation?.id, deleteAutomationFn]);

	return (
		<NavBarWrapper>
			<NavBarBreadcrumbWrapper>
				{automation === undefined ? (
					<BreadcrumbsSkeleton />
				) : (
					<Breadcrumbs
						crumbs={[
							{ title: 'Automations', href: '/automations' },
							{
								title: automation.title || 'Untitled',
								href: `/automation/${automation.id}`,
							},
						]}
					/>
				)}
			</NavBarBreadcrumbWrapper>
			<NavBarRightSideWrapper>
				<Group spacing="xs" noWrap>
					<Group spacing="3xs">
						<AutomationToggle
							enabled={automation?.is_enabled ?? false}
							onChange={handleAutomationStatusToggle}
						/>
						{hasDeletePermission && (
							<Menu position="bottom-end">
								<Menu.Target>
									<IconButton
										tooltip="More controls"
										iconName="dots"
										variant="tertiary"
									/>
								</Menu.Target>
								<Menu.Dropdown>
									<Menu.Item
										icon={<Icon name="trash" />}
										onClick={handleDeleteAutomation}
									>
										Delete
									</Menu.Item>
								</Menu.Dropdown>
							</Menu>
						)}
					</Group>
					{automation && (
						<Tooltip
							label="Can’t run when automation is paused"
							disabled={automation?.is_enabled ?? undefined}
						>
							<Box>
								<RunAutomationButton
									automationId={automation?.id}
									navigateToHistory={navigateToHistory}
									disabled={!automation?.is_enabled}
								/>
							</Box>
						</Tooltip>
					)}
				</Group>
			</NavBarRightSideWrapper>
			<NavBarSidesheetControlsWrapper>
				<EntityPageSidebarToggle />
			</NavBarSidesheetControlsWrapper>
		</NavBarWrapper>
	);
}

const AutomationPageNavBarComponent = observer(AutomationPageNavBar);
export default AutomationPageNavBarComponent;
